import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type BalancePaymentPageProps = {
  apiBaseUrl: string;
  paypalClientId?: string;
  token: string;
  paypalOrderId?: string;
  paymentCancelled?: boolean;
};

type BalanceSessionData = {
  token: string;
  orderId: string;
  travelName: string;
  travelDestination: string | null;
  customerFullName: string;
  customerEmail: string;
  balanceAmountValue: string;
  balanceCurrencyCode: string;
  balancePaymentState: string;
  expiresAt: string | null;
};

type BalancePaymentResult = {
  orderId: string;
  orderStatus: string;
  captureId: string | null;
  captureStatus: string;
  paymentState: "PAID" | "PENDING";
  message: string;
};

export default function BalancePaymentPage({
  apiBaseUrl,
  paypalClientId,
  token,
  paypalOrderId,
  paymentCancelled = false
}: BalancePaymentPageProps) {
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<BalanceSessionData | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentResult, setPaymentResult] = useState<BalancePaymentResult | null>(null);
  const captureTriggeredRef = useRef(false);

  const normalizedPayPalClientId = (paypalClientId || "").trim();
  const paypalEnabled = normalizedPayPalClientId.length > 0;
  const normalizedPayPalOrderId = (paypalOrderId || "").trim();
  const isReturnCaptureFlow = normalizedPayPalOrderId.length > 0 && !paymentCancelled;
  const paymentMessage = useMemo(() => {
    if (!paymentResult) {
      return "";
    }

    const trimmed = paymentResult.message.trim();
    if (trimmed) {
      return trimmed;
    }
    return paymentResult.paymentState === "PAID"
      ? "Balance payment completed successfully."
      : "Balance payment is pending confirmation.";
  }, [paymentResult]);
  const simulatedCaptureState = useMemo(() => {
    const raw = new URLSearchParams(window.location.search)
      .get("simulateCaptureState")
      ?.trim()
      .toUpperCase();
    if (
      raw === "PENDING" ||
      raw === "COMPLETED" ||
      raw === "DECLINED" ||
      raw === "DENIED" ||
      raw === "FAILED"
    ) {
      return raw;
    }
    return "";
  }, []);

  useEffect(() => {
    let disposed = false;
    setSessionLoading(true);
    setSessionError(null);
    setSessionData(null);

    void fetch(
      `${apiBaseUrl}/api/public/balance-payment/session?token=${encodeURIComponent(token)}`
    )
      .then(async (response) => {
        const payload = (await response
          .json()
          .catch(() => null)) as
          | {
              success?: boolean;
              data?: BalanceSessionData;
              error?: string;
            }
          | null;

        if (!response.ok || !payload?.success || !payload.data) {
          throw new Error(payload?.error || "Failed to load balance payment link.");
        }

        if (!disposed) {
          setSessionData(payload.data);
        }
      })
      .catch((error) => {
        if (!disposed) {
          setSessionError(
            error instanceof Error ? error.message : "Failed to load balance payment link."
          );
        }
      })
      .finally(() => {
        if (!disposed) {
          setSessionLoading(false);
        }
      });

    return () => {
      disposed = true;
    };
  }, [apiBaseUrl, token]);

  const capturePayPalOrder = useCallback(
    async (orderId: string) => {
      setPaymentError(null);
      setPaymentPending(true);
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/public/balance-payment/paypal/capture-order`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              token,
              orderId,
              simulatePaymentState: simulatedCaptureState || undefined
            })
          }
        );

        const payload = (await response
          .json()
          .catch(() => null)) as
          | {
              success?: boolean;
              data?: {
                orderId?: string;
                orderStatus?: string;
                captureId?: string | null;
                captureStatus?: string;
                paymentState?: string;
                message?: string;
              };
              error?: string;
            }
          | null;

        if (!response.ok || !payload?.success || !payload.data?.paymentState) {
          throw new Error(payload?.error || "Failed to capture PayPal payment.");
        }

        const paymentState = (payload.data.paymentState || "").toUpperCase();
        if (paymentState !== "PAID" && paymentState !== "PENDING") {
          throw new Error("Unexpected payment state returned from server.");
        }

        setPaymentResult({
          orderId: payload.data.orderId || orderId,
          orderStatus: (payload.data.orderStatus || "").toUpperCase(),
          captureId: payload.data.captureId || null,
          captureStatus: (payload.data.captureStatus || "").toUpperCase(),
          paymentState,
          message:
            payload.data.message ||
            (paymentState === "PAID"
              ? "Balance payment completed successfully."
              : "Balance payment is pending confirmation.")
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to capture PayPal payment.";
        setPaymentError(message);
        throw error;
      } finally {
        setPaymentPending(false);
      }
    },
    [apiBaseUrl, simulatedCaptureState, token]
  );

  const createAndRedirectToPayPal = useCallback(async () => {
    setPaymentError(null);
    setPaymentPending(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/public/balance-payment/paypal/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ token })
      });

      const payload = (await response
        .json()
        .catch(() => null)) as
        | {
            success?: boolean;
            data?: { orderId?: string; approveUrl?: string };
            error?: string;
          }
        | null;

      if (!response.ok || !payload?.success || !payload.data?.approveUrl) {
        throw new Error(payload?.error || "Failed to create PayPal order.");
      }

      window.location.assign(payload.data.approveUrl);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create PayPal order.";
      setPaymentError(message);
      setPaymentPending(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    if (!sessionData || !isReturnCaptureFlow || paymentResult || paymentError || paymentPending) {
      return;
    }
    if (captureTriggeredRef.current) {
      return;
    }

    captureTriggeredRef.current = true;
    void capturePayPalOrder(normalizedPayPalOrderId).catch(() => {
      // Error is shown via state.
    });
  }, [
    capturePayPalOrder,
    isReturnCaptureFlow,
    normalizedPayPalOrderId,
    paymentError,
    paymentPending,
    paymentResult,
    sessionData
  ]);

  return (
    <section className="mx-auto w-full max-w-[920px] px-4 py-10 md:px-8 lg:px-10">
      <div className="rounded-3xl bg-white p-8 shadow-[0_18px_40px_rgba(15,23,36,0.12)]">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#f7b500]">
          Balance Payment
        </p>

        {sessionLoading ? (
          <p className="mt-4 text-sm text-[#6b7280]">Loading payment link...</p>
        ) : sessionError || !sessionData ? (
          <div className="mt-4 rounded-2xl border border-[#fecaca] bg-[#fff1f2] p-4 text-sm text-[#b91c1c]">
            {sessionError || "This balance payment link is unavailable."}
          </div>
        ) : (
          <>
            <h1 className="mt-3 text-2xl font-semibold text-[#111827] md:text-3xl">
              {sessionData.travelName}
            </h1>
            <p className="mt-2 text-sm text-[#6b7280]">
              Customer: {sessionData.customerFullName} ({sessionData.customerEmail})
            </p>
            {sessionData.travelDestination && (
              <p className="mt-1 text-sm text-[#6b7280]">
                Destination: {sessionData.travelDestination}
              </p>
            )}

            <div className="mt-6 rounded-2xl bg-[#0b1c2b] px-5 py-4 text-white">
              <p className="text-xs uppercase tracking-[0.28em] text-white/70">Amount Due</p>
              <p className="mt-2 text-3xl font-semibold">
                {sessionData.balanceCurrencyCode} {sessionData.balanceAmountValue}
              </p>
            </div>

            {paymentResult ? (
              <div
                className={`mt-6 rounded-2xl border px-5 py-4 ${
                  paymentResult.paymentState === "PAID"
                    ? "border-[#86efac] bg-[#f0fdf4]"
                    : "border-[#fde68a] bg-[#fffbeb]"
                }`}
              >
                <p className="text-base font-semibold text-[#111827]">
                  {paymentResult.paymentState === "PAID"
                    ? "Payment completed"
                    : "Payment pending"}
                </p>
                <p className="mt-2 text-sm text-[#111827]">{paymentMessage}</p>
                <p className="mt-2 text-xs text-[#4b5563]">
                  Order: {paymentResult.orderId}
                  {paymentResult.captureId ? ` · Capture: ${paymentResult.captureId}` : ""}
                </p>
              </div>
            ) : isReturnCaptureFlow ? (
              <div className="mt-6 rounded-2xl border border-[#dbeafe] bg-[#eff6ff] px-5 py-4">
                <p className="text-base font-semibold text-[#1e3a8a]">Finalizing payment...</p>
                <p className="mt-2 text-sm text-[#1e40af]">
                  {paymentPending
                    ? "Verifying your PayPal payment, please wait."
                    : "We are confirming your payment status."}
                </p>
                {paymentError && (
                  <>
                    <p className="mt-3 text-sm text-[#b91c1c]">{paymentError}</p>
                    <button
                      type="button"
                      onClick={() => {
                        captureTriggeredRef.current = true;
                        void capturePayPalOrder(normalizedPayPalOrderId).catch(() => {
                          // Error is shown via state.
                        });
                      }}
                      className="mt-3 rounded-xl border border-[#1d4ed8] bg-white px-4 py-2 text-xs font-semibold text-[#1d4ed8] transition hover:bg-[#dbeafe]"
                    >
                      Retry confirmation
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                {sessionData.expiresAt && (
                  <p className="mt-4 text-xs text-[#6b7280]">
                    Link expires at {new Date(sessionData.expiresAt).toLocaleString("zh-CN")}.
                  </p>
                )}

                {paymentCancelled && (
                  <div className="mt-4 rounded-2xl border border-[#fde68a] bg-[#fffbeb] px-4 py-3 text-xs text-[#92400e]">
                    Payment was cancelled on PayPal. You can click the button below to try again.
                  </div>
                )}

                <label className="mt-5 flex items-start gap-3 text-xs text-[#5f6b7a]">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(event) => setAgreed(event.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-[#d1d5db]"
                  />
                  <span>I confirm this is the remaining balance payment for this booking.</span>
                </label>

                <div className="mt-6 space-y-3">
                  {!paypalEnabled ? (
                    <button
                      type="button"
                      disabled
                      className="w-full rounded-xl bg-[#e5e7eb] px-5 py-3 text-xs font-semibold text-[#6b7280]"
                    >
                      PayPal is not configured
                    </button>
                  ) : !agreed ? (
                    <button
                      type="button"
                      disabled
                      className="w-full rounded-xl bg-[#e5e7eb] px-5 py-3 text-xs font-semibold text-[#6b7280]"
                    >
                      Confirm agreement to continue
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        void createAndRedirectToPayPal();
                      }}
                      disabled={paymentPending}
                      className="w-full rounded-xl bg-[#ffc439] px-5 py-3 text-xs font-semibold text-[#111827] transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {paymentPending ? "Redirecting to PayPal..." : "Continue to PayPal"}
                    </button>
                  )}

                  {paymentPending && (
                    <p className="text-xs text-[#0d5be1]">Redirecting to PayPal secure checkout...</p>
                  )}
                  {paymentError && <p className="text-xs text-[#ef4444]">{paymentError}</p>}
                  {simulatedCaptureState && (
                    <p className="text-xs text-[#b45309]">
                      Local debug mode: capture will simulate {simulatedCaptureState}.
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
